/**
 * Global MUI theme.
 *
 * The visual language is "modern slate dashboard": deep navy primary
 * for chrome (sidebar / header / charts), bright accent blue for CTAs
 * and emphasis, neutral cool greys for surfaces. Component overrides
 * standardise corner radii, shadows and spacing across cards / tables /
 * chips so individual pages don't have to fiddle with sx props.
 *
 * Touch this file to retheme the whole app.
 */

import { createTheme, alpha } from '@mui/material/styles';

// ---- palette ---------------------------------------------------------------

const COLORS = {
  // Primary slate (navy)
  primary50: '#EEF2FF',
  primary100: '#DBE4FF',
  primary500: '#3357FF',  // accent blue
  primary600: '#2541DC',
  primary700: '#1E2C8A',
  primary900: '#0F172A',  // deep navy -- used for typography contrast

  // Greys
  grey50:  '#F8FAFC',
  grey100: '#F1F5F9',
  grey200: '#E2E8F0',
  grey300: '#CBD5E1',
  grey400: '#94A3B8',
  grey500: '#64748B',
  grey700: '#334155',
  grey900: '#0F172A',

  // Status
  success: '#16A34A',
  warning: '#D97706',
  error:   '#DC2626',
  info:    '#0284C7',
};

// ---- platform colour map (used by PlatformChip) ----------------------------

export const PLATFORM_COLORS = {
  ios:       '#0A84FF',
  android:   '#16A34A',
  web:       '#9333EA',
  youtube:   '#DC2626',
  instagram: '#C2185B',
  twitter:   '#1F2937',
};

// ---- typography ------------------------------------------------------------

const typography = {
  fontFamily: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    'Segoe UI',
    'Roboto',
    'Helvetica',
    'Arial',
    'sans-serif',
  ].join(','),
  h1: { fontWeight: 700, letterSpacing: '-0.02em' },
  h2: { fontWeight: 700, letterSpacing: '-0.02em' },
  h3: { fontWeight: 700, letterSpacing: '-0.02em' },
  h4: { fontWeight: 700, letterSpacing: '-0.015em', fontSize: '1.75rem' },
  h5: { fontWeight: 600, letterSpacing: '-0.01em' },
  h6: { fontWeight: 600, letterSpacing: '-0.01em' },
  subtitle1: { fontWeight: 600 },
  subtitle2: { fontWeight: 600 },
  body1: { lineHeight: 1.6 },
  body2: { lineHeight: 1.6 },
  button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
  overline: { letterSpacing: '0.08em', fontWeight: 600 },
};

// ---- theme -----------------------------------------------------------------

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      light: COLORS.primary100,
      main: COLORS.primary500,
      dark: COLORS.primary700,
      contrastText: '#fff',
    },
    secondary: {
      main: COLORS.primary900,
    },
    success: { main: COLORS.success },
    warning: { main: COLORS.warning },
    error:   { main: COLORS.error },
    info:    { main: COLORS.info },
    background: {
      default: COLORS.grey50,
      paper: '#fff',
    },
    text: {
      primary: COLORS.primary900,
      secondary: COLORS.grey500,
    },
    divider: COLORS.grey200,
    grey: {
      50: COLORS.grey50,
      100: COLORS.grey100,
      200: COLORS.grey200,
      300: COLORS.grey300,
      400: COLORS.grey400,
      500: COLORS.grey500,
      700: COLORS.grey700,
      900: COLORS.grey900,
    },
  },
  shape: {
    borderRadius: 12,
  },
  typography,

  // Soft shadows -- avoid heavy default MUI elevation
  shadows: [
    'none',
    '0 1px 2px 0 rgba(15, 23, 42, 0.04)',
    '0 1px 3px 0 rgba(15, 23, 42, 0.06), 0 1px 2px -1px rgba(15, 23, 42, 0.04)',
    '0 4px 6px -1px rgba(15, 23, 42, 0.07), 0 2px 4px -2px rgba(15, 23, 42, 0.05)',
    '0 10px 15px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -4px rgba(15, 23, 42, 0.06)',
    ...Array(20).fill('0 20px 25px -5px rgba(15, 23, 42, 0.10), 0 8px 10px -6px rgba(15, 23, 42, 0.08)'),
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: COLORS.grey50,
          '&::-webkit-scrollbar': { width: 10, height: 10 },
          '&::-webkit-scrollbar-thumb': {
            background: COLORS.grey300,
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-thumb:hover': { background: COLORS.grey400 },
        },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          color: COLORS.primary900,
          borderBottom: `1px solid ${COLORS.grey200}`,
        },
      },
    },

    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          borderColor: COLORS.grey200,
          borderRadius: 16,
          boxShadow: 'none',
          transition: 'border-color 120ms ease, box-shadow 120ms ease',
          '&:hover': {
            borderColor: COLORS.grey300,
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': { paddingBottom: 20 },
        },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        outlined: {
          borderColor: COLORS.grey200,
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingInline: 16,
          paddingBlock: 8,
        },
        containedPrimary: {
          boxShadow: `0 1px 2px ${alpha(COLORS.primary500, 0.4)}`,
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          color: COLORS.grey500,
          '&:hover': { backgroundColor: COLORS.grey100, color: COLORS.primary500 },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
        },
        sizeSmall: {
          height: 22,
          fontSize: 11,
        },
        outlined: {
          borderColor: COLORS.grey200,
          backgroundColor: COLORS.grey50,
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
        indicator: { height: 3, borderRadius: 3 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          fontWeight: 600,
          textTransform: 'none',
          letterSpacing: 0,
          paddingInline: 18,
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: COLORS.grey200,
        },
        head: {
          backgroundColor: COLORS.grey50,
          color: COLORS.grey700,
          fontWeight: 700,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        },
      },
    },

    MuiAccordion: {
      defaultProps: { elevation: 0, disableGutters: true },
      styleOverrides: {
        root: {
          border: `1px solid ${COLORS.grey200}`,
          borderRadius: 12,
          backgroundColor: '#fff',
          marginBottom: 8,
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: '0 0 8px 0',
            borderColor: alpha(COLORS.primary500, 0.4),
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          paddingInline: 16,
          minHeight: 56,
          '&.Mui-expanded': { minHeight: 56 },
        },
        content: {
          margin: '12px 0',
          '&.Mui-expanded': { margin: '12px 0' },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 16,
          paddingTop: 0,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12 },
        standardInfo: {
          backgroundColor: alpha(COLORS.info, 0.08),
          color: COLORS.info,
        },
        standardWarning: {
          backgroundColor: alpha(COLORS.warning, 0.1),
          color: COLORS.warning,
        },
        standardError: {
          backgroundColor: alpha(COLORS.error, 0.1),
          color: COLORS.error,
        },
        standardSuccess: {
          backgroundColor: alpha(COLORS.success, 0.1),
          color: COLORS.success,
        },
      },
    },

    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#fff',
          },
        },
      },
    },

    // DataGrid global polish
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${COLORS.grey200}`,
          borderRadius: 12,
          backgroundColor: '#fff',
          fontSize: 13,
          '--DataGrid-rowBorderColor': COLORS.grey200,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: COLORS.grey50,
            borderBottom: `1px solid ${COLORS.grey200}`,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            color: COLORS.grey700,
            textTransform: 'uppercase',
            fontSize: 11,
            letterSpacing: '0.04em',
          },
          '& .MuiDataGrid-cell': {
            borderTopColor: COLORS.grey100,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: alpha(COLORS.primary500, 0.04),
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${COLORS.grey200}`,
          },
          '& .MuiDataGrid-toolbarContainer': {
            padding: 8,
            gap: 8,
            borderBottom: `1px solid ${COLORS.grey200}`,
          },
        },
      },
    },
  },
});

export default theme;
